import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";

import { useLocation, useNavigate } from "react-router-dom";

import Filter from "../../../assets/icons/FilterA.svg";
import { App } from "../../../routes/Constants";
import APIVariables from "../../../services/api-variables";
import Loader from "../../common/Loader/Loader";
import { handleCatch, useQuery } from "../../../shared/helpers";
import ToggleSwitch from "../../common/ToggleSwitch/ToggleSwitch";
import useRoles from "../../../hooks/useRoles";
import { useClickOutside } from "../../../hooks/useClickOutside";
import Axios from "../../../services/Axios";
import DataTable from "@/global/tables/DataTable";
import {
  getPrescriptionNotificationColumns,
  getAdminNotificationColumns,
} from "./NotificationsColumns";

const Notifications = () => {
  const filterRef = useRef(null);
  const query = useQuery();
  const navigate = useNavigate();
  const location = useLocation();

  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState([]);
  const [filtersAdded, setFiltersAdded] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [searchAdminValue, setSearchAdminValue] = useState("");
  const [adminNotifications, setAdminNotifications] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isFilters, setIsFilters] = useState(false);
  const queryFilters = useMemo(
    () => query.getAll("status").map((filter) => parseInt(filter, 10)),
    [query]
  );
  const { isPrescriber, isPharmacy, isHomecare, isOutpatient } = useRoles();

  const controller = useRef(new AbortController());

  const [isLoadingTable, setIsLoadingTable] = useState(true);
  const [isLoadingAdminTable, setIsLoadingAdminTable] = useState(true);
  const currentReadingNotifications = useRef([]);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [pageIndexAdmin, setPageIndexAdmin] = useState(0);
  const [pageSizeAdmin, setPageSizeAdmin] = useState(10);
  const [totalRowsAdmin, setTotalRowsAdmin] = useState(0);

  const prescriptionColumns = useMemo(
    () => getPrescriptionNotificationColumns(),
    []
  );
  const adminColumns = useMemo(() => getAdminNotificationColumns(), []);

  const normalizeListResponse = (data) => {
    if (Array.isArray(data)) {
      return {
        results: data,
        count: data.length,
        next: null,
      };
    }
    return {
      results: data?.results ?? [],
      count: data?.count,
      next: data?.next,
    };
  };

  const loadPrescriptionNotifications = useCallback(
    async (pageIdx, size) => {
      if (controller.current) {
        controller.current.abort();
        controller.current = new AbortController();
      }
      setIsLoadingTable(true);
      try {
        const params = new URLSearchParams();
        if (searchValue) params.set("search", searchValue);
        queryFilters.forEach((num) => params.append("status", String(num)));
        params.set("page", String(pageIdx + 1));
        params.set("page_size", String(size));

        const res = await Axios.get(
          `${APIVariables.PRESCRIPTIONS_NOTIFICATIONS}?${params.toString()}`,
          { signal: controller.current.signal }
        ).catch((error) => {
          handleCatch(error);
        });

        if (res?.status === 200) {
          setIsLoading(false);
          const normalized = normalizeListResponse(res.data);
          const results = normalized.results;
          setItems(results);
          setFilters(res.data?.filter_data?.status ?? []);
          const count = normalized.count;
          const hasNext = !!normalized.next;
          setTotalRows(
            count != null
              ? count
              : hasNext
                ? (pageIdx + 1) * size + 1
                : pageIdx * size + results.length
          );
        }
      } finally {
        setTimeout(() => {
          setIsLoadingTable(false);
        }, 500);
      }
    },
    [searchValue, queryFilters]
  );

  const loadAdminNotifications = useCallback(
    async (pageIdx, size) => {
      setIsLoadingAdminTable(true);
      try {
        const params = new URLSearchParams();
        if (searchAdminValue) params.set("search", searchAdminValue);
        params.set("page", String(pageIdx + 1));
        params.set("page_size", String(size));

        const res = await Axios.get(
          `${APIVariables.ADMIN_NOTIFICATIONS}?${params.toString()}`
        ).catch((error) => {
          handleCatch(error);
        });

        if (res?.status === 200) {
          if (Array.isArray(res.data)) {
            const all = res.data;
            setTotalRowsAdmin(all.length);
            setAdminNotifications(
              all.slice(pageIdx * size, pageIdx * size + size)
            );
          } else {
            const normalized = normalizeListResponse(res.data);
            const results = normalized.results;
            setAdminNotifications(results);
            const count = normalized.count;
            const hasNext = !!normalized.next;
            setTotalRowsAdmin(
              count != null
                ? count
                : hasNext
                  ? (pageIdx + 1) * size + 1
                  : pageIdx * size + results.length
            );
          }
        }
      } finally {
        setTimeout(() => {
          setIsLoadingAdminTable(false);
        }, 500);
      }
    },
    [searchAdminValue]
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setPageIndex(0);
      loadPrescriptionNotifications(0, pageSize);
    }, 500);
    return () => clearTimeout(delayDebounce);
    // pageSize changes are applied only via DataTable onPageSizeChange (server pagination).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, location.search, loadPrescriptionNotifications]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setPageIndexAdmin(0);
      loadAdminNotifications(0, pageSizeAdmin);
    }, 500);
    return () => clearTimeout(delayDebounce);
    // pageSizeAdmin changes are applied only via DataTable onPageSizeChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchAdminValue, loadAdminNotifications]);

  const handleServerFilterChange = useCallback((tableFilters) => {
    if (tableFilters?.search !== undefined) {
      setSearchValue(String(tableFilters.search ?? ""));
    }
    setPageIndex(0);
  }, []);

  const handleAdminServerFilterChange = useCallback((tableFilters) => {
    if (tableFilters?.search !== undefined) {
      setSearchAdminValue(String(tableFilters.search ?? ""));
    }
    setPageIndexAdmin(0);
  }, []);

  const markAsReadTheNotification = async (notificationID, isRead, name) => {
    if (currentReadingNotifications.current.includes(notificationID)) return;

    currentReadingNotifications.current.push(notificationID);

    if (isRead === false) {
      const res = await Axios.post(
        name === "admin_notification"
          ? APIVariables.PRESCRIPTIONS_ADMIN_NOTIFICATIONS_READ + notificationID
          : APIVariables.PRESCRIPTIONS_NOTIFICATIONS_READ + notificationID,
        {}
      )
        .catch(function (error) {
          handleCatch(error);
        })
        .finally(() => {
          document.dispatchEvent(new CustomEvent("fetchNotificationsCount"));
        });

      if (res?.status === 200) {
        if (name === "admin_notification") {
          setAdminNotifications((prev) =>
            prev.map((el) =>
              el.id === notificationID ? { ...el, read: true } : el
            )
          );
        } else {
          setItems((prev) =>
            prev.map((element) =>
              element.id === notificationID
                ? { ...element, read: true }
                : element
            )
          );
        }
      }
    }
    currentReadingNotifications.current =
      currentReadingNotifications.current.filter((el) => el !== notificationID);
  };

  const onChange = (value) => {
    if (filtersAdded.includes(value))
      setFiltersAdded((prevState) =>
        prevState.filter((item) => item !== value)
      );
    else setFiltersAdded((prevState) => prevState.concat(value));
  };

  const setAllFiltersOn = () => {
    setFiltersAdded([]);
    filters?.forEach((filter) =>
      setFiltersAdded((prev) => prev.concat(filter.id))
    );
  };

  const handleFilter = () => {
    const filter = filtersAdded.map((num) => `status=${num}`).join("&");
    setIsFilters(false);
    setPageIndex(0);
    navigate(App.NOTIFICATIONS + (filter ? `?${filter}` : ""));
  };

  useClickOutside(filterRef, handleFilter);

  const notificationClick = (row, name) => {
    if (name === "admin_notification") {
      markAsReadTheNotification(row.id, row.read, "admin_notification");
    } else {
      markAsReadTheNotification(row.id, row.read);
    }

    if (
      name !== "admin_notification" &&
      (isPharmacy || isHomecare || isOutpatient)
    ) {
      navigate(
        App.PRESCRIPTION_AUTHORISATION_FORM_DEFAULT +
        `${row.patient_id}` +
        "/" +
        `${row.prescription_id}`
      );
    }
  };

  const [scrollToBottom, setScrollToBottom] = useState(true);

  const scrolToBottom = async () => {
    await new Promise((resolve) => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth",
      });
      const handleScroll = () => {
        if (window.innerHeight + window.scrollY >= document.body.scrollHeight) {
          window.removeEventListener("scroll", handleScroll);
          resolve();
        }
      };
      window.addEventListener("scroll", handleScroll);
    });

    setScrollToBottom(false);
  };

  if (
    location.search.includes("?scrollToBottom=true") &&
    window.scrollY === 0 &&
    scrollToBottom === true
  ) {
    setTimeout(scrolToBottom, 0);
  }

  return (
    <div
      className="min-h-screen bg-[#f8f8f8] pb-16 pt-10"
      data-testid="notifications-page"
    >
      {isLoading ? (
        <div data-testid="notifications-loader">
          <Loader />
        </div>
      ) : (
        <div className="mx-auto px-8" data-testid="notifications-content">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
            <h1
              className="text-xl font-medium leading-9"
              data-testid="notifications-heading"
            >
              {isPrescriber
              ? "Prescriber Notifications"
              : isPharmacy
                ? "Pharmacist Notifications"
                : isHomecare
                  ? "Connected Homecare Notifications"
                  : isOutpatient
                    ? "Connected Outpatient Notifications"
                    : "Connected Nurse Notifications"}
            </h1>
            {!(isHomecare || isOutpatient) && (
              <div
                className="relative flex flex-col items-end"
                data-testid="notifications-filter-section"
              >
                <button
                  type="button"
                  className="flex cursor-pointer items-center gap-1.5 border-0 bg-transparent text-xl font-medium leading-9 text-[#3c3c3c]"
                  onClick={() => setIsFilters(!isFilters)}
                  data-testid="notifications-filter-button"
                  aria-label="Filter notifications"
                >
                  <img
                    src={Filter}
                    alt=""
                    width={23}
                    height={21}
                    className="h-[21px] w-[23px]"
                    data-testid="notifications-filter-icon"
                  />
                </button>

                {isFilters && (
                  <div
                    className="absolute right-0 top-full z-99999 mt-2 w-64 rounded-md bg-white p-5 shadow-[0_0_40px_rgba(0,0,0,0.2)]"
                    ref={filterRef}
                    data-testid="notifications-filter-panel"
                  >
                    <div
                      className="flex flex-col gap-2.5 text-[#3c3c3c]"
                      data-testid="notifications-filter-list"
                    >
                      {(filters || []).map((data, index) => (
                        <div
                          className="flex items-center justify-between"
                          key={index}
                          data-testid={`notifications-filter-item-${data.id}`}
                        >
                          <div data-testid={`notifications-filter-label-${data.id}`}>
                            {data.value}
                          </div>
                          <div
                            className="flex items-center gap-1"
                            data-testid={`notifications-filter-toggle-row-${data.id}`}
                          >
                            <span data-testid={`notifications-filter-state-${data.id}`}>
                              {filtersAdded.includes(data.id) ? "On" : "Off"}
                            </span>{" "}
                            <ToggleSwitch
                              name={data.value}
                              id={data.id}
                              small
                              disabled={false}
                              checked={filtersAdded.includes(data.id)}
                              onChange={onChange}
                            />
                          </div>
                        </div>
                      ))}
                      <hr
                        className="my-0 w-full border-0 border-t border-[#e6e6e6]"
                        data-testid="notifications-filter-divider"
                      />
                      <div
                        className="flex justify-around text-base"
                        data-testid="notifications-filter-actions"
                      >
                        <button
                          type="button"
                          className="cursor-pointer border-0 bg-transparent text-[#3c3c3c]"
                          onClick={setAllFiltersOn}
                          data-testid="notifications-filter-all-on-button"
                        >
                          All on
                        </button>
                        <button
                          type="button"
                          className="cursor-pointer border-0 bg-transparent text-[#00567a]"
                          onClick={handleFilter}
                          data-testid="notifications-filter-done-button"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            className="mb-10 overflow-hidden rounded-md bg-white shadow-[0_4px_20px_rgba(0,0,0,0.1)]"
            data-testid="notifications-prescription-section"
          >
            <div
              className="bg-white px-4 pb-4"
              data-testid="notifications-prescription-table"
            >
              <DataTable
                columns={prescriptionColumns}
                data={items}
                onRowClick={(row) => notificationClick(row)}
                loading={isLoadingTable}
                getRowTestId={(row) => `notifications-row-${row?.id}`}
                serverPagination={{
                  enabled: true,
                  pageIndex,
                  pageSize,
                  totalRows,
                  onPageChange: (newIndex) => {
                    setPageIndex(newIndex);
                    loadPrescriptionNotifications(newIndex, pageSize);
                  },
                  onPageSizeChange: (newSize) => {
                    setPageSize(newSize);
                    setPageIndex(0);
                    loadPrescriptionNotifications(0, newSize);
                  },
                  onFilterChange: handleServerFilterChange,
                }}
              />
            </div>
          </div>

          <div className="mt-16" data-testid="notifications-admin-section">
            <h1
              className="text-xl font-medium leading-9"
              data-testid="notifications-admin-heading"
            >
              General Notifications
            </h1>
            <div
              className="rounded-md border border-[#e6e6e6] bg-white px-4 pb-4 pt-2 shadow-sm mt-4"
              data-testid="notifications-admin-table"
            >
              <DataTable
                columns={adminColumns}
                data={adminNotifications ?? []}
                onRowClick={(row) =>
                  notificationClick(row, "admin_notification")
                }
                loading={isLoadingAdminTable}
                getRowTestId={(row) => `notifications-admin-row-${row?.id}`}
                serverPagination={{
                  enabled: true,
                  pageIndex: pageIndexAdmin,
                  pageSize: pageSizeAdmin,
                  totalRows: totalRowsAdmin,
                  onPageChange: (newIndex) => {
                    setPageIndexAdmin(newIndex);
                    loadAdminNotifications(newIndex, pageSizeAdmin);
                  },
                  onPageSizeChange: (newSize) => {
                    setPageSizeAdmin(newSize);
                    setPageIndexAdmin(0);
                    loadAdminNotifications(0, newSize);
                  },
                  onFilterChange: handleAdminServerFilterChange,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default Notifications;
